import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import FormField from "../VendorOnboarding/FormField";
import useApiHandler from "../../hooks/ApiHandler";
import { Property, Unit } from "../../../service";
import { useUser } from "../../context/UserContext";
import AddPropertyForm from "../common/properties/AddProperty";
import { formatAddress } from "../../constant/Home/Jobs";

const Step3 = ({ propertyFromState }) => {
  const { values, setFieldValue } = useFormikContext();
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [unitsData, setUnitsData] = useState([]); // Store the original units data
  const { callApi, loading } = useApiHandler();
  const { userData } = useUser();

  // Add Property modal state
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [addedPropertyId, setAddedPropertyId] = useState(null);

  // Shared fetch to populate property options
  const fetchProperties = async () => {
    await callApi(() => Property.getProperties(), {
      onSuccess: (response) => {
        const transformedOptions = response.data?.results?.map((property) => ({
          value: property.id,
          label: `${property.name}, ${property.streetAddress}`,
          raw: property,
        }));
        setPropertyOptions(transformedOptions || []);
      },
      onError: (error) => {
        console.error("Error fetching properties:", error);
        setPropertyOptions([]);
      },
      successMessage: "Properties loaded successfully",
      errorMessage: "Failed to load properties",
    });
  };

  useEffect(() => {
    // Skip fetching properties for tenants as they get property info from getUnits API
    if (userData?.accountType === "TENANT") {
      return;
    }

    // If property is passed from navigation state, use it directly
    if (propertyFromState) {
      const propertyOption = {
        value: propertyFromState.id,
        label: `${propertyFromState.name}, ${propertyFromState.streetAddress}`,
      };
      setPropertyOptions([propertyOption]);
      setFieldValue("property", propertyFromState.id);
      return;
    }

    fetchProperties();
  }, [userData?.accountType, propertyFromState]);

  // Refetch and select newly added property when modal closes
  useEffect(() => {
    if (!showAddPropertyModal && addedPropertyId) {
      (async () => {
        await fetchProperties();
        setFieldValue("property", addedPropertyId);
        // Clear tracking id after selecting
        setAddedPropertyId(null);
      })();
    }
  }, [showAddPropertyModal, addedPropertyId]);

  // Fetch units for tenants using getUnits API
  useEffect(() => {
    if (userData?.accountType === "TENANT") {
      const fetchUnits = async () => {
        await callApi(() => Unit.getUnits(), {
          onSuccess: (response) => {
            if (response.data) {
              // Store the original units data
              setUnitsData(response.data?.results);

              // Transform units to dropdown options format
              const transformedUnitOptions = response.data?.results?.map(
                (unit) => ({
                  value: unit.id,
                  label: unit.unitApt || unit.unit_apt || `Unit ${unit.id}`,
                })
              );

              setUnitOptions(transformedUnitOptions || []);

              // Get unique properties from all units
              const uniqueProperties = [
                ...new Map(
                  response.data?.results?.map((unit) => [
                    unit.property.id,
                    {
                      value: unit.property.id,
                      label: unit.property.name,
                    },
                  ])
                ).values(),
              ];

              setPropertyOptions(uniqueProperties || []);

              // Auto-select the first unit if no unit is currently selected
              if (!values.unit && response.data?.results?.length > 0) {
                const firstUnit = response.data?.results[0];
                setFieldValue("unit", firstUnit.id);
                // Also set the property for the first unit
                if (firstUnit.property) {
                  setFieldValue("property", firstUnit.property.id);
                }
              }
            }
          },
          onError: (error) => {
            console.error("Error fetching units:", error);
            setUnitOptions([]);
            setPropertyOptions([]);
            setUnitsData([]);
          },
          successMessage: "Units loaded successfully",
          errorMessage: "Failed to load units",
        });
      };

      fetchUnits();
    }
  }, [userData?.accountType]);

  // Handle unit change for tenants - update property when unit changes
  useEffect(() => {
    if (
      userData?.accountType === "TENANT" &&
      values.unit &&
      unitsData?.length > 0
    ) {
      const selectedUnit = unitsData?.find(
        (unit) => unit.id === Number(values.unit)
      );
      if (selectedUnit && selectedUnit.property) {
        const propertyInfo = selectedUnit.property;
        setFieldValue("property", propertyInfo.id);
      }
    }
  }, [values.unit, userData?.accountType, unitsData]);

  // Fetch property details and units when property changes and user is not a tenant
  useEffect(() => {
    if (
      values.property &&
      userData?.accountType !== "TENANT"
    ) {
      const fetchPropertyDetails = async () => {
        await callApi(() => Property.getPropertyById(values.property), {
          onSuccess: (response) => {
            const propertyData = response.data;

            if (propertyData && propertyData.units) {
              // Transform units to dropdown options format
              const transformedUnitOptions = propertyData.units.map((unit) => ({
                value: unit.id,
                label: unit.unit_apt || unit.unitApt || `Unit ${unit.id}`,
              }));

              setUnitOptions(transformedUnitOptions);

              // Auto-select the first unit if there's only one and no unit is currently selected (Landlords only)
              if (
                ["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType) &&
                transformedUnitOptions.length === 1 &&
                !values.unit
              ) {
                setFieldValue("unit", transformedUnitOptions[0].value);
              }
            } else {
              setUnitOptions([]);
            }
          },
          onError: (error) => {
            console.error("Error fetching property details:", error);
            setUnitOptions([]);
          },
          successMessage: "Property details loaded successfully",
          errorMessage: "Failed to load property details",
        });
      };

      fetchPropertyDetails();
    }
  }, [values.property, userData?.accountType]);

  useEffect(() => {
    const selectedProperty = propertyOptions.find(
      (option) => String(option.value) === String(values.property)
    );
    if (selectedProperty) {
      setFieldValue("property_label", selectedProperty.label);
    }
  }, [propertyOptions, setFieldValue, values.property]);

  useEffect(() => {
    if (!values.unit) {
      setFieldValue("unit_label", "");
      return;
    }
    const selectedUnit = unitOptions.find(
      (option) => String(option.value) === String(values.unit)
    );
    if (selectedUnit) {
      setFieldValue("unit_label", selectedUnit.label);
    }
  }, [unitOptions, setFieldValue, values.unit]);

  return (
    <div className=" flex justify-center items-center w-full">
      <div className="w-full rounded-[16px]  flex flex-col md:gap-4 gap-3 items-center   ">
        <div className="w-full">
          {/* Removed checkbox + autofill message */}
          <div className="space-y-3 w-full">
            <div>
              <FormField
                label="Property Address:"
                name="property"
                type="select"
                placeholder={
                  loading ? "Loading properties..." : "Select Property Address"
                }
                options={propertyOptions.map(({ raw, ...rest }) => rest)}
                disabled={loading || userData?.accountType === "TENANT"}
              />
            </div>
            {(["LANDLORD", "PROPERTY_MANAGER"].includes(userData?.accountType) ||
              userData?.accountType === "TENANT") && (
              <div>
                <FormField
                  label="Select Unit:"
                  name="unit"
                  type="select"
                  placeholder={loading ? "Loading units..." : "Select Unit"}
                  options={unitOptions}
                />
              </div>
            )}
          </div>
          <div className="flex self-start items-center mt-3">
            <h4 className={'mr-1'}>
              Don't see your address?
            </h4>
            <h4
              className="text-[#3CAB5C] cursor-pointer underline font-medium"
              onClick={() => setShowAddPropertyModal(true)}
            >
              Add Property
            </h4>
          </div>
        </div>
      </div>

      {showAddPropertyModal && (
        <AddPropertyForm
          setModalState={() => setShowAddPropertyModal(false)}
          propertyId={null}
          onSuccess={() => {
            // Close modal immediately; after AddPropertyForm sets property id via setPropertyId,
            // the effect will refresh and select the new property.
            setShowAddPropertyModal(false);
          }}
          setPropertyId={(id) => setAddedPropertyId(id)}
        />
      )}
    </div>
  );
};

export default Step3;
